import React, { FC, PropsWithChildren, useCallback, useRef, useState } from 'react';
import { Transition } from 'react-transition-group';
import classNames from 'classnames';

import styles from './Expander.module.scss';

// ---

export const ANIMATION_DURATION = 300;
const INITIAL_HEIGHT = 'initial';

type ExpanderProps = PropsWithChildren<{
  isOpen: boolean;
  duration?: number;
  className?: string;
}>;

export const Expander: FC<ExpanderProps> = ({
  isOpen,
  duration = ANIMATION_DURATION,
  className,
  children,
}) => {
  const expanderHeight = useRef(0);

  const [maxHeight, setMaxHeight] = useState(isOpen ? INITIAL_HEIGHT : '0px');

  const onEntering = useCallback((expander: HTMLElement) => {
    const height = expander.firstElementChild?.clientHeight || 0;

    expanderHeight.current = height;
    setMaxHeight(`${height}px`);
  }, []);

  const onEntered = useCallback(() => {
    // Чтобы при ресайзе экспандер не заезжал на другие блоки
    setMaxHeight(INITIAL_HEIGHT);
  }, []);

  const onExit = useCallback(() => {
    setMaxHeight(`${expanderHeight.current}px`);
  }, []);

  const onExiting = useCallback(() => {
    setTimeout(() => setMaxHeight('0px'), 0);
  }, []);

  return (
    <Transition unmountOnExit
                appear
                in={isOpen}
                timeout={duration}
                onEntering={onEntering}
                onEntered={onEntered}
                onExit={onExit}
                onExiting={onExiting}>
      <div className={classNames(styles.expander, className)}
           style={{
             maxHeight,
             overflow: maxHeight === INITIAL_HEIGHT ? 'visible' : 'hidden',
             transitionDuration: `${duration}ms`,
           }}>
        {children}
      </div>
    </Transition>
  );
};
